import {GetServerSidePropsContext} from "next";
import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";
import React, {CSSProperties, ReactElement, ReactNode, useEffect, useMemo, useRef, useState} from "react";
import {twMerge} from "tailwind-merge";
import {NextPageWithLayout} from "@pages/_app";
import {Plugin} from "@hooks/plugins/plugin";
import {IPluginsDataResponse} from "@hooks/plugins/IPluginsDataResponse";
import Link from "@components/elements/DocumentLink";
import Image from "next/image";
import {StarRating} from "@components/elements/StarRating";
import {MdOutlineRateReview} from "react-icons/md";
import {BiLinkExternal} from "react-icons/bi";
import {
    FaArrowLeft,
    FaCalendar,
    FaCheck,
    FaCog,
    FaDownload,
    FaGlobe,
    FaHistory,
    FaImages,
    FaInfo,
    FaInfoCircle,
    FaLanguage,
    FaPhp,
    FaPlug,
    FaQuestion,
    FaStar,
    FaTag,
    FaUser,
    FaUsers,
    FaWordpress
} from "react-icons/fa";
import {Chip} from "@components/elements/Chip";
import {ModalBody} from "@components/elements/ModalBody";
import {ModalHeader} from "@components/elements/ModalHeader";
import {EModalMode} from "@components/elements/EModalMode";
import {Layout} from "@components/layouts/layout";
import {Overlay} from "@components/elements/Overlay";
import {Modal} from "@components/elements/Modal";
import {CgTranscript} from "react-icons/cg";
import {usePluginReviews} from "@hooks/plugins/usePluginReviews";
import {PluginsGetter} from "../../server/api/data/PluginsGetter";
import {getDb} from "../../server/api/db";
import {getPluginMaintenanceInsights} from "@utils/pluginInsights";
import {getAuthorPageHref, getPluginAuthorSlug} from "@utils/wordpress";
import {load as loadHtml} from "cheerio";
import {runAfterResponse} from "@utils/afterResponse";
import {formatDate, formatNumber, formatPrice} from "@utils/format";
import {readPluginsListState} from "@utils/pluginsListState";
import {SiWoocommerce} from "react-icons/si";
import {useStableNow} from "@hooks/useStableNow";
import {CACHE_WARM_HEADERS, shouldWarmCache} from "@utils/cacheWarm";


const addLazyLoadingToScreenshots = (screenshotsHtml: string): string => {
    if (!screenshotsHtml || !screenshotsHtml.trim()) {
        return screenshotsHtml;
    }

    const $ = loadHtml(screenshotsHtml);
    $("img").each((_index, element) => {
        const img = $(element);
        img.attr("loading", "lazy");
        img.attr("decoding", "async");
        img.attr("fetchpriority", "low");
    });
    return $("body").html() ?? screenshotsHtml;
};


export const getServerSideProps = async (context: GetServerSidePropsContext) => {
    // Set CDN cache headers: cache for 1 hour, serve stale for 1 day while revalidating
    context.res.setHeader(
        'Cache-Control',
        'public, s-maxage=3600, stale-while-revalidate=86400'
    );

    const pluginId = context.params?.id as string | undefined;
    if (!pluginId) {
        return {
            notFound: true,
        }
    }

    try {
        const renderedAt = Date.now();

        const db = getDb();
        const getter = new PluginsGetter();
        const response: IPluginsDataResponse = await getter.get({
            db,
            pluginid: pluginId,
            page: 1,
            per_page: 1,
        }) as IPluginsDataResponse;

        const plugin = response.data?.plugins?.[0];

        if (!plugin) {
            console.error('No plugin data found in response');
            return {
                notFound: true,
            }
        }

        const pluginWithLazyScreenshots = {
            ...plugin,
            screenshots: addLazyLoadingToScreenshots(plugin.screenshots),
        };

        const trimAlternativePlugin = (candidate: any) => {
            const trimmed = {...candidate};
            trimmed.banners = undefined;
            trimmed.changelog = undefined;
            trimmed.description = undefined;
            trimmed.faq = undefined;
            trimmed.installation = undefined;
            trimmed.reviews = [];
            trimmed.screenshots = undefined;
            trimmed.icons = Array.isArray(trimmed.icons) ? trimmed.icons.slice(0, 1) : trimmed.icons;
            return trimmed;
        };

        const tagSlugs = Array.isArray(plugin.tags)
            ? plugin.tags
                .map((tag: any) => ({
                    slug: String(tag.slug ?? ""),
                    count: Number(tag.count ?? tag.plugins_count ?? 0),
                }))
                .filter((tag: any) => tag.slug)
                .sort((a: any, b: any) => (a.count || 0) - (b.count || 0))
                .map((tag: any) => tag.slug)
            : [];

        const desiredAlternatives = 6;
        const alternatives: any[] = [];
        const seenPluginIds = new Set<string>([pluginId]);
        const tagSetSizes = [3, 2, 1];

        for (const size of tagSetSizes) {
            if (alternatives.length >= desiredAlternatives) break;
            const tags = tagSlugs.slice(0, size);
            if (!tags.length) continue;

            const alternativesResponse: IPluginsDataResponse = await getter.get({
                db,
                tags,
                page: 1,
                per_page: Math.max(10, desiredAlternatives * 2),
            }) as IPluginsDataResponse;

            for (const candidate of alternativesResponse.data?.plugins ?? []) {
                const candidateId = String(candidate?.pluginid ?? "");
                if (!candidateId || seenPluginIds.has(candidateId)) continue;
                seenPluginIds.add(candidateId);
                alternatives.push(trimAlternativePlugin(candidate));
                if (alternatives.length >= desiredAlternatives) break;
            }
        }

        return {
            props: {
                pluginName: pluginWithLazyScreenshots.name,
                pluginId: pluginWithLazyScreenshots.pluginid,
                stringifyPluginData: JSON.stringify(pluginWithLazyScreenshots),
                shortDescription: pluginWithLazyScreenshots.short_description,
                version: pluginWithLazyScreenshots.version ?? null,
                homepage: pluginWithLazyScreenshots.homepage ?? null,
                wordpressUrl: pluginWithLazyScreenshots.wordpress_link?.url ?? null,
                authorName: pluginWithLazyScreenshots.author ?? null,
                authorProfileUrl: pluginWithLazyScreenshots.author_profile?.url ?? null,
                marketplace: pluginWithLazyScreenshots.marketplace ?? null,
                marketplaceUrl: pluginWithLazyScreenshots.marketplace_url ?? null,
                price: pluginWithLazyScreenshots.price ?? null,
                rating: pluginWithLazyScreenshots.rating,
                numRatings: pluginWithLazyScreenshots.num_ratings,
                activeInstalls: pluginWithLazyScreenshots.active_installs,
                downloaded: pluginWithLazyScreenshots.downloaded ?? null,
                lastUpdatedTimestamp: pluginWithLazyScreenshots.last_updated?.timestamp ?? null,
                minimumWordpressVersion: pluginWithLazyScreenshots.minimum_wordpress_version ?? null,
                testedWordpressVersion: pluginWithLazyScreenshots.last_wordpress_compatible_version ?? null,
                minimumPhpVersion: pluginWithLazyScreenshots.minimum_php_version ?? null,
                supportThreads: pluginWithLazyScreenshots.support_threads ?? null,
                supportThreadsResolved: pluginWithLazyScreenshots.support_threads_resolved ?? null,
                stringifyAlternativesData: JSON.stringify(alternatives),
                renderedAt,
            },
        }
    } catch (error) {
        console.error('Error fetching plugin data:', error);
        return {
            notFound: true,
        }
    } finally {
        // Warm CDN cache for related pages after response is sent
        const baseUrl = process.env.NEXT_PUBLIC_WEBSITE_URL;
        if (baseUrl && pluginId && shouldWarmCache(context.req)) {
            runAfterResponse(context.res, async () => {
                const urlsToWarm = [
                    `${baseUrl}/alternatives-to/${pluginId}`,
                ];

                await Promise.allSettled(
                    urlsToWarm.map(url =>
                        fetch(url, { method: 'GET', headers: CACHE_WARM_HEADERS }).catch(() => {})
                    )
                );
            });
        }
    }
}



const PluginPage: NextPageWithLayout = (props: any) => {
    const now = useStableNow(props.renderedAt);
    const [isOpen, setIsOpen] = useState(false);
    const [pluginData, setPluginData] = useState<IProcessedPluginData | undefined>(() => {
        if (props.stringifyPluginData) {
            try {
                const parsedData = JSON.parse(props.stringifyPluginData);
                return Plugin.processApiResponse(parsedData, now ?? props.renderedAt);
            } catch (error) {
                console.error('Error parsing plugin data:', error);
                return undefined;
            }
        }
        return undefined;
    });

    useEffect(() => {
        if (props.stringifyPluginData) {
            try {
                const parsedData = JSON.parse(props.stringifyPluginData);
                setPluginData(Plugin.processApiResponse(parsedData, now ?? props.renderedAt));
            } catch (error) {
                console.error('Error parsing plugin data:', error);
            }
        }
    }, [props.stringifyPluginData, now, props.renderedAt]);

    const [backHref, setBackHref] = useState<string>('/plugins');

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const stored = readPluginsListState();
        const fallbackHref = pluginData?.pluginid ? `/plugins#${pluginData.pluginid}` : '/plugins';
        if (stored?.url) {
            setBackHref(stored.url);
            return;
        }
        setBackHref(fallbackHref);
    }, [pluginData?.pluginid]);

    const alternativePlugins = useMemo<IProcessedPluginData[]>(() => {
        if (!props.stringifyAlternativesData) return [];
        try {
            const parsed = JSON.parse(props.stringifyAlternativesData);
            if (!Array.isArray(parsed)) return [];
            return parsed.map((item) => Plugin.processApiResponse(item, now ?? props.renderedAt));
        } catch (error) {
            console.error('Error parsing alternatives data:', error);
            return [];
        }
    }, [props.stringifyAlternativesData, now, props.renderedAt]);

    // If we don't have plugin data, show an error
    if (!pluginData) {
        return <div className="flex items-center justify-center h-full">
            <div className="text-center">
                <h2 className="text-xl font-semibold">Plugin Not Found</h2>
                <p className="text-gray-500">The plugin you&apos;re looking for doesn&apos;t exist or couldn&apos;t be loaded</p>
                <Link href="/plugins" className="text-blue-500 hover:underline mt-4 inline-block">
                    Return to plugins list
                </Link>
            </div>
        </div>
    }

    const maintenanceInsights = getPluginMaintenanceInsights(pluginData, now ?? props.renderedAt);
    const nowTimestamp = typeof now === "number" ? now : (typeof props.renderedAt === "number" ? props.renderedAt : null);
    const daysSincePluginUpdate = Number.isFinite(pluginData.last_updated?.getTime?.()) && nowTimestamp !== null
        ? Math.max(0, Math.floor((nowTimestamp - pluginData.last_updated.getTime()) / (1000 * 60 * 60 * 24)))
        : null;
    const pluginAgeYears = Number.isFinite(pluginData.added?.getTime?.()) && nowTimestamp !== null
        ? Math.max(0, (nowTimestamp - pluginData.added.getTime()) / (1000 * 60 * 60 * 24 * 365))
        : null;
    const authorSlug = getPluginAuthorSlug(pluginData);
    const marketplace = pluginData.marketplace;
    const marketplaceLabel = marketplace === 'woocommerce' ? 'WooCommerce' : 'WordPress';
    const marketplaceListingLabel = marketplace === 'woocommerce' ? 'WooCommerce Marketplace' : 'WordPress.org';
    const MarketplaceIcon = marketplace === 'woocommerce' ? SiWoocommerce : FaWordpress;
    const listingUrl = pluginData.wordpress_link?.url;
    const priceLabel = formatPrice(pluginData.price);
    const showCompatibility = marketplace !== 'woocommerce';
    const maintenanceVerdictLabel: Record<string, string> = {
        "actively-maintained": "Actively maintained",
        "maintained": "Maintained",
        "stale": "Stale",
        "possibly-abandoned": "Possibly abandoned",
    };
    const supportResolutionPercent = maintenanceInsights.supportResolutionRate === null
        ? null
        : Math.round(maintenanceInsights.supportResolutionRate * 100);
    const daysSinceUpdate = maintenanceInsights.daysSinceUpdate;
    const maintenanceBarClassName = maintenanceInsights.score >= 75
        ? "bg-green-500"
        : maintenanceInsights.score >= 50
            ? "bg-yellow-500"
            : "bg-red-500";
    const abandonedCheckLabel = maintenanceInsights.verdict === "possibly-abandoned"
        ? "Possibly abandoned"
        : "Likely maintained";

    const infoPoints: Array<{content: ReactNode; icon: any; title: string; link?: string}> = [];

    if (pluginData) {
        infoPoints.push(
            {
                content: <>Total downloads: {formatNumber(pluginData.downloaded)}</>,
                icon: FaDownload,
                title: 'The number of times the plugin has been downloaded during its lifetime.',
                link: listingUrl
            },
            {
                content: <>Active installs: {formatNumber(pluginData.active_installs)}</>,
                icon: FaPlug,
                title: 'The number of active installations of the plugin during the last 30 days.',
                link: listingUrl
            },
            {
                content: <>Total reviews: {formatNumber(pluginData.num_ratings)}</>,
                icon: MdOutlineRateReview,
                title: 'The number of reviews the plugin has received.',
                link: listingUrl
            },
            {
                content: <>Average rating: {formatNumber(pluginData.rating)}</>,
                icon: FaStar,
                title: 'The average rating of the plugin.',
                link: listingUrl
            },
        );

        if (priceLabel) {
            infoPoints.push({
                content: <>Price: {priceLabel}</>,
                icon: FaTag,
                title: `The current price listed on ${marketplaceListingLabel}.`,
                link: listingUrl,
            });
        }

        infoPoints.push(
            {
                content: <>Support threads opened: {formatNumber(pluginData.support_threads)}</>,
                icon: FaQuestion,
                title: 'The number of support threads opened for the plugin.',
            },
            {
                content: <>Support threads
                    resolved: {formatNumber(pluginData.support_threads_resolved)} ({pluginData.support_threads_resolved
                        ? formatNumber(Number((pluginData.support_threads_resolved / pluginData.support_threads * 100).toFixed(0)))
                        : formatNumber(0)}%)</>,
                icon: FaCheck,
                title: 'The number of support threads resolved for the plugin.',
            },
            {
                content: <>Available in: {pluginData.languages?.length + 1} language(s)</>,
                icon: FaGlobe,
                title: 'The number of languages the plugin is available in.',
            },
            {
                content: <>Contributors: {pluginData.contributors?.length}</>,
                icon: FaUsers,
                title: 'The number of contributors to the plugin.',
            },
            {
                content: <>Last
                    updated: {formatDate(pluginData.last_updated)}{daysSincePluginUpdate === null ? '' : ` (${daysSincePluginUpdate} days ago)`}</>,
                icon: FaHistory,
                title: 'The date the plugin was last updated.',
            },
            {
                content: <>Added to {marketplaceLabel}: {formatDate(pluginData.added)}{pluginAgeYears === null ? '' : ` (${Math.floor(pluginAgeYears)} years old)`}</>,
                icon: FaCalendar,
                title: `The date the plugin was added to ${marketplaceListingLabel}.`,
            },
        );

        if (showCompatibility) {
            infoPoints.push(
                {
                    content: <>Minimum WordPress version: {pluginData.minimum_wordpress_version}</>,
                    icon: FaWordpress,
                    title: 'The minimum version of WordPress required to run the plugin.'
                },
                {
                    content: <>Tested up to WordPress version: {pluginData.last_wordpress_compatible_version}</>,
                    icon: FaWordpress,
                    title: 'The last version of WordPress the plugin has been tested with.'
                },
                {
                    content: <>Minimum PHP version: {pluginData.minimum_php_version}</>,
                    icon: FaPhp,
                    title: 'The minimum version of PHP required to run the plugin.'
                },
            );
        }
    }

    const tocItems = [
        {title: 'Actions', id: 'actions'},
        {title: 'Plugin info', id: 'plugin-info'},
        {title: 'Maintenance', id: 'maintenance'},
        {title: 'Ratings', id: 'reviews'},
        {title: 'Review feed', id: 'reviews-list'},
        {title: 'Developers', id: 'developers'},
        {title: 'Languages', id: 'languages'},
        {title: 'Alternatives', id: 'alternatives'},
        {title: 'Description', id: 'description'},
        {title: 'Installation', id: 'installation'},
        {title: 'FAQ', id: 'faq'},
        {title: 'Screenshots', id: 'screenshots'},
        {title: 'Changelog', id: 'changelog'},
    ];

    const actionItems: Array<{title: string; icon: any; link: string}> = [];

    if (listingUrl) {
        actionItems.push({
            title: marketplace === 'woocommerce' ? 'View listing' : 'Download',
            icon: marketplace === 'woocommerce' ? MarketplaceIcon : FaDownload,
            link: listingUrl,
        });
    }

    actionItems.push({
        title: 'Author page',
        icon: FaUser,
        link: authorSlug ? getAuthorPageHref(authorSlug) : (pluginData.author_profile.url || ''),
    });

    if (listingUrl && marketplace === 'wordpress') {
        actionItems.push({
            title: `See on ${marketplaceLabel}`,
            icon: MarketplaceIcon,
            link: listingUrl,
        });
    }
    const visibleActionItems = actionItems.filter((item) => item.link);

    return (
        <div className="relative">
            <div
                className="pointer-events-none absolute inset-x-0 top-[-10rem] h-[22rem] bg-gradient-to-br from-indigo-100 via-sky-50 to-transparent opacity-80 blur-3xl dark:from-indigo-900/40 dark:via-slate-900 dark:to-transparent dark:opacity-80"
                aria-hidden="true"
            />

            <div className="mx-auto w-full max-w-7xl p-4 md:p-8">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <Link
                        href={backHref}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-800"
                    >
                        <FaArrowLeft className="h-4 w-4" aria-hidden="true"/> Back to plugins
                    </Link>

                    <div className="flex flex-wrap items-center gap-2">
                        {pluginData.homepage && (
                            <a
                                href={pluginData.homepage}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-200 dark:hover:bg-gray-800"
                            >
                                <FaGlobe className="h-4 w-4" aria-hidden="true"/> Homepage
                                <BiLinkExternal className="h-4 w-4" aria-hidden="true"/>
                            </a>
                        )}
                        {pluginData.wordpress_link?.url && (
                            <a
                                href={pluginData.wordpress_link.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400"
                            >
                                <MarketplaceIcon className="h-4 w-4" aria-hidden="true"/> {marketplaceLabel}
                                <BiLinkExternal className="h-4 w-4" aria-hidden="true"/>
                            </a>
                        )}
                    </div>
                </div>

                <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12">
                    <aside className="hidden lg:block lg:col-span-3">
                        <div className="sticky top-6 space-y-4">
                            <div className="rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/40">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    On this page
                                </div>
                                <nav className="mt-3 flex flex-col gap-1">
                                    {tocItems.map((item) => (
                                        <a
                                            key={item.id}
                                            href={`#${item.id}`}
                                            className="rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-50 hover:text-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                                        >
                                            {item.title}
                                        </a>
                                    ))}
                                </nav>
                            </div>

                            <div className="rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/40">
                                <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                    Summary
                                </div>
                                <div className="mt-3 space-y-2 text-sm text-gray-700 dark:text-gray-200">
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Maintenance</span>
                                        <span className="font-semibold">{maintenanceInsights.score}/100</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Last updated</span>
                                        <span className="font-semibold">{daysSincePluginUpdate === null ? '—' : `${daysSincePluginUpdate}d ago`}</span>
                                    </div>
                                    <div className="flex items-center justify-between">
                                        <span className="text-gray-500 dark:text-gray-400">Active installs</span>
                                        <span className="font-semibold">{formatNumber(pluginData.active_installs) || '—'}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </aside>

                    <div className="lg:col-span-9 flex flex-col gap-6">
                        <header
                            id="header"
                            className="rounded-3xl border border-gray-200 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/40 md:p-8"
                        >
                            <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
                                <div className="flex items-start gap-4">
                                    <div
                                        className="relative h-14 w-14 shrink-0 overflow-hidden rounded-2xl border border-gray-200 bg-gray-100 shadow-sm dark:border-gray-700 dark:bg-gray-800 sm:h-16 sm:w-16 md:h-20 md:w-20 aspect-square"
                                        title={pluginData.name}
                                    >
                                        {pluginData.icons?.length > 0 ? (
                                            <Image
                                                src={pluginData.icons?.[0]?.url}
                                                alt={pluginData.name}
                                                fill
                                                className="object-contain"
                                                unoptimized={true}
                                            />
                                        ) : (
                                            <div className="flex h-full w-full items-center justify-center">
                                                <FaPlug className="h-8 w-8 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                                            </div>
                                        )}
                                    </div>

                                    <div className="flex flex-col gap-2">
                                        <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100 md:text-4xl">
                                            {pluginData.name}
                                        </h1>
                                        {pluginData.short_description && (
                                            <p className="max-w-2xl text-sm text-gray-700 dark:text-gray-300 md:text-base">
                                                {pluginData.short_description}
                                            </p>
                                        )}
                                        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                                            <div className="flex items-center gap-2">
                                                <StarRating rating={pluginData.rating} maxRating={5}/>
                                                <span>({formatNumber(pluginData.num_ratings) || '0'})</span>
                                            </div>
                                            <span className="text-gray-300 dark:text-gray-700" aria-hidden="true">•</span>
                                            {(() => {
                                                const authorLabel = pluginData.htmlStrippedAuthor || pluginData.author;
                                                if (!authorSlug) {
                                                    return <span className="truncate">by {authorLabel}</span>;
                                                }

                                                return (
                                                    <Link
                                                        href={getAuthorPageHref(authorSlug)}
                                                        className="truncate font-medium hover:underline"
                                                    >
                                                        by {authorLabel}
                                                    </Link>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-wrap items-start gap-2 md:justify-end">
                                    <span className="inline-flex items-center gap-2 rounded-full bg-indigo-50 px-3 py-2 text-sm font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                                        Maintenance {maintenanceInsights.score}/100
                                    </span>
                                    <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                        {daysSincePluginUpdate === null ? '—' : `${daysSincePluginUpdate}d`} since update
                                    </span>
                                    {priceLabel && (
                                        <span className="inline-flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2 text-sm font-semibold text-gray-700 dark:bg-gray-800 dark:text-gray-200">
                                            <FaTag className="h-4 w-4" aria-hidden="true"/>
                                            {priceLabel}
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4">
                                <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900">
                                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                        <FaPlug className="h-4 w-4" aria-hidden="true"/>
                                        <span className="text-xs font-semibold uppercase tracking-wide">Active installs</span>
                                    </div>
                                    <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {formatNumber(pluginData.active_installs) || '—'}
                                    </div>
                                </div>
                                <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900">
                                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                        <FaDownload className="h-4 w-4" aria-hidden="true"/>
                                        <span className="text-xs font-semibold uppercase tracking-wide">Downloads</span>
                                    </div>
                                    <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {formatNumber(pluginData.downloaded) || '—'}
                                    </div>
                                </div>
                                <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900">
                                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                        <MdOutlineRateReview className="h-4 w-4" aria-hidden="true"/>
                                        <span className="text-xs font-semibold uppercase tracking-wide">Reviews</span>
                                    </div>
                                    <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {formatNumber(pluginData.num_ratings) || '—'}
                                    </div>
                                </div>
                                <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900">
                                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                        <FaCalendar className="h-4 w-4" aria-hidden="true"/>
                                        <span className="text-xs font-semibold uppercase tracking-wide">Age</span>
                                    </div>
                                    <div className="mt-2 text-lg font-semibold text-gray-900 dark:text-gray-100">
                                        {pluginAgeYears === null ? '—' : `${pluginAgeYears.toFixed(1)}y`}
                                    </div>
                                </div>
                            </div>

                            {pluginData.tags?.length > 0 && (
                                <div className="mt-6 flex flex-wrap items-center gap-2">
                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                        Tags
                                    </span>
                                    {pluginData.tags.slice(0, 16).map((item, index) => (
                                        <Chip
                                            key={`${item.getSlug?.() ?? item.slug}-${index}`}
                                            href={`/tag/${item.getSlug()}`}
                                            value={item.getLabel?.() ?? item.getValue()}
                                            slug={item.getSlug()}
                                            className="mx-0 text-xs"
                                        />
                                    ))}
                                    {pluginData.tags.length > 16 && (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">+{pluginData.tags.length - 16}</span>
                                    )}
                                </div>
                            )}
                        </header>

                        <Section title="Actions" icon={<FaCog/>} id="actions">
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 sm:gap-3">
                                {visibleActionItems.map((item) => {
                                    const isExternal = item.link.startsWith("http");
                                    const linkClassName = "group flex min-w-0 items-center justify-between gap-2 rounded-2xl border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-gray-800 shadow-sm transition hover:-translate-y-0.5 hover:border-indigo-300 hover:bg-indigo-50/40 hover:shadow-md dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100 dark:hover:border-indigo-500 dark:hover:bg-indigo-900/20 sm:gap-3 sm:px-4 sm:py-3 sm:text-sm";
                                    const linkContent = (
                                        <>
                                            <span className="flex min-w-0 flex-1 items-center gap-2">
                                                <item.icon className="h-4 w-4 text-gray-500 transition group-hover:text-indigo-600 dark:text-gray-400 dark:group-hover:text-indigo-300 sm:h-5 sm:w-5" aria-hidden="true"/>
                                                <span className="min-w-0 break-words">{item.title}</span>
                                            </span>
                                            <BiLinkExternal className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500 sm:h-4 sm:w-4" aria-hidden="true"/>
                                        </>
                                    );

                                    if (isExternal) {
                                        return (
                                            <a
                                                key={item.title}
                                                href={item.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={linkClassName}
                                            >
                                                {linkContent}
                                            </a>
                                        );
                                    }

                                    return (
                                        <Link key={item.title} href={item.link} className={linkClassName}>
                                            {linkContent}
                                        </Link>
                                    );
                                })}
                            </div>
                        </Section>

                        <Section title="Plugin info" icon={<FaInfo/>} id="plugin-info">
                            <div className="grid grid-cols-2 gap-2 sm:grid-cols-2 sm:gap-3 md:grid-cols-3">
                                {infoPoints.map((item, index) => (
                                    <div
                                        key={index}
                                        title={item.title}
                                        className="flex min-w-0 items-start gap-2 rounded-2xl border border-gray-200 bg-white p-3 text-xs shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:gap-3 sm:p-4 sm:text-sm"
                                    >
                                        <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 sm:h-9 sm:w-9">
                                            <item.icon className="h-3.5 w-3.5 sm:h-4 sm:w-4" aria-hidden="true"/>
                                        </div>
                                        <div className="min-w-0 flex-1 break-words text-gray-700 dark:text-gray-200">{item.content}</div>
                                    </div>
                                ))}
                            </div>
                        </Section>

                        <Section title="Maintenance & Compatibility" icon={<FaHistory/>} id="maintenance">
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-col gap-2">
                                    <div className="flex flex-wrap items-start justify-between gap-4">
                                        <div className="flex flex-col gap-1">
                                            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 md:text-lg">
                                                Maintenance score
                                            </h3>
                                            <p className="text-xs text-gray-600 dark:text-gray-400 md:text-sm">
                                                {maintenanceVerdictLabel[maintenanceInsights.verdict]}
                                                {daysSinceUpdate !== null ? ` • Last updated ${daysSinceUpdate} days ago` : ""}
                                                {supportResolutionPercent !== null ? ` • Support resolved ${supportResolutionPercent}%` : ""}
                                                {pluginData.num_ratings ? ` • ${formatNumber(pluginData.num_ratings)} reviews` : ""}
                                            </p>
                                        </div>
                                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100 md:text-2xl">
                                            {maintenanceInsights.score}/100
                                        </div>
                                    </div>
                                    <div className="h-2 w-full rounded-full bg-gray-200 dark:bg-gray-800">
                                        <div
                                            className={twMerge("h-2 rounded-full", maintenanceBarClassName)}
                                            style={{width: `${maintenanceInsights.score}%`}}
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                                    <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900">
                                        <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                                            <FaQuestion className="h-4 w-4" aria-hidden="true"/>
                                            <h3>Is {pluginData.name} abandoned?</h3>
                                        </div>
                                        <p className="mt-2 text-sm text-gray-700 dark:text-gray-300">
                                            {abandonedCheckLabel}
                                            {daysSinceUpdate !== null ? ` (last update ${daysSinceUpdate} days ago).` : "."}
                                        </p>
                                    </div>
                                    {showCompatibility && (
                                        <div className="rounded-2xl border border-gray-200 bg-white p-4 text-sm shadow-sm dark:border-gray-700 dark:bg-gray-900">
                                            <div className="flex items-center gap-2 font-semibold text-gray-900 dark:text-gray-100">
                                                <FaWordpress className="h-4 w-4" aria-hidden="true"/>
                                                <h3>Compatibility</h3>
                                            </div>
                                            <div className="mt-2 flex flex-col gap-1 text-sm text-gray-700 dark:text-gray-300">
                                                <div>Requires WordPress: {pluginData.minimum_wordpress_version || "Unknown"}</div>
                                                <div>Tested up to: {pluginData.last_wordpress_compatible_version || "Unknown"}</div>
                                                <div>Requires PHP: {pluginData.minimum_php_version || "Unknown"}</div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </Section>

                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                            <Section title="Ratings" icon={<FaStar/>} id="reviews" maxHeight="260px">
                                <div className="flex flex-col gap-2 text-sm">
                                    {(['5', '4', '3', '2', '1'] as const).map((rating) => (
                                        <a
                                            key={rating}
                                            href={pluginData.ratings.links[rating].url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex items-center justify-between rounded-lg px-2 py-2 transition hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            <StarRating rating={Number(rating)} maxRating={5}/>
                                            <span className="font-semibold text-gray-700 dark:text-gray-200">
                                                {formatNumber(pluginData.ratings[rating])}
                                            </span>
                                        </a>
                                    ))}
                                    <a
                                        href={pluginData.ratings.links.all.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-2 text-center text-sm font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                                    >
                                        See all reviews
                                    </a>
                                </div>
                            </Section>

                            <Section title="Developers" icon={<FaUsers/>} id="developers" maxHeight="260px">
                                <div className="flex flex-col gap-2 text-sm">
                                    {pluginData.contributors
                                        .slice()
                                        .sort((a, b) => a.is_owner ? -1 : (b.is_owner ? 1 : b.count - a.count))
                                        .map((contributor, index) => (
                                            <Link
                                                href={`/author/${contributor.slug}`}
                                                key={`${contributor.slug}-${index}`}
                                                className="flex items-center justify-between rounded-lg px-2 py-2 text-sm transition hover:bg-gray-50 dark:hover:bg-gray-800"
                                            >
                                                <span className="truncate text-gray-700 dark:text-gray-200">
                                                    {contributor.name + (contributor.is_owner ? " (Owner)" : "")}
                                                </span>
                                                <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{formatNumber(contributor.count)}</span>
                                            </Link>
                                        ))}
                                </div>
                            </Section>

                            <Section title="Languages" icon={<FaLanguage/>} id="languages" maxHeight="260px">
                                <div className="flex flex-col gap-2 text-sm">
                                    {pluginData.languages.map((language, index) => (
                                        <Link
                                            href={`/language/${language.getSlug()}`}
                                            key={`${language.getSlug()}-${index}`}
                                            className="flex items-center justify-between rounded-lg px-2 py-2 text-sm transition hover:bg-gray-50 dark:hover:bg-gray-800"
                                        >
                                            <span className="truncate text-gray-700 dark:text-gray-200">{language.getLabel()}</span>
                                            <span className="text-xs font-semibold text-gray-500 dark:text-gray-400">{formatNumber(language.count) || "—"}</span>
                                        </Link>
                                    ))}
                                </div>
                            </Section>
                        </div>

                        <Section title="Similar & Alternatives" icon={<FaPlug/>} id="alternatives">
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                                Explore plugins with similar tags, and compare key metrics like downloads, ratings, updates, support, and WP/PHP compatibility.
                            </p>
                            <div className="mt-4 flex flex-col gap-2">
                                {alternativePlugins.length > 0 ? alternativePlugins.map((plugin) => (
                                    <div
                                        key={plugin.pluginid}
                                        className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 md:flex-row md:items-center md:justify-between"
                                    >
                                        <div className="flex flex-col gap-1">
                                            <Link
                                                href={`/plugins/${plugin.pluginid}`}
                                                className="font-semibold text-gray-900 hover:underline dark:text-gray-100"
                                            >
                                                {plugin.name}
                                            </Link>
                                            <div className="text-xs text-gray-600 dark:text-gray-400">
                                                Rating {plugin.rating.toFixed(1)}/5 ({formatNumber(plugin.num_ratings)} reviews)
                                                {" • "}Active installs {formatNumber(plugin.active_installs)}
                                            </div>
                                        </div>
                                        <div className="flex flex-row items-center gap-4">
                                            <Link
                                                href={`/compare/${pluginData.pluginid}-vs-${plugin.pluginid}`}
                                                className="text-sm font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                                            >
                                                Compare
                                            </Link>
                                        </div>
                                    </div>
                                )) : (
                                    <div className="text-sm text-gray-500 dark:text-gray-400">
                                        No similar plugins found yet.
                                    </div>
                                )}
                            </div>
                            <div className="mt-3">
                                <Link
                                    href={`/alternatives-to/${pluginData.pluginid}`}
                                    className="text-sm font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                                >
                                    See more alternatives to {pluginData.name}
                                </Link>
                            </div>
                        </Section>

                        <Section
                            title="Description"
                            icon={<FaInfoCircle/>}
                            id="description"
                            maxHeight="320px"
                            bodyClassName={twMerge("prose prose-sm sm:prose md:prose-lg dark:prose-invert")}
                            bodyStyle={{maxWidth: "100%"}}
                        >
                            {(pluginData?.description && pluginData.description.trim().length > 0 &&
                                    <div dangerouslySetInnerHTML={{__html: pluginData.description ?? ""}}/>)
                                || <div className="text-center text-gray-500 dark:text-gray-400">No description available</div>}
                        </Section>

                        <Section
                            title="Installation"
                            icon={<CgTranscript/>}
                            id="installation"
                            maxHeight="320px"
                            bodyClassName={twMerge("prose prose-sm sm:prose md:prose-lg dark:prose-invert")}
                            bodyStyle={{maxWidth: "100%"}}
                        >
                            {(pluginData.installation && pluginData.installation.trim().length > 0 &&
                                    <div dangerouslySetInnerHTML={{__html: pluginData.installation ?? ""}}/>)
                                || <div className="text-center text-gray-500 dark:text-gray-400">No installation instructions available</div>}
                        </Section>

                        <Section
                            title="Frequently Asked Questions"
                            icon={<FaQuestion/>}
                            id="faq"
                            maxHeight="320px"
                            bodyClassName={twMerge("prose prose-sm sm:prose md:prose-lg dark:prose-invert")}
                            bodyStyle={{maxWidth: "100%"}}
                        >
                            {(pluginData.faq && pluginData.faq.trim().length > 0 &&
                                    <div dangerouslySetInnerHTML={{__html: pluginData.faq ?? ""}}/>)
                                || <div className="text-center text-gray-500 dark:text-gray-400">No FAQ available</div>}
                        </Section>

                        <Section title="Review feed" icon={<MdOutlineRateReview/>} id="reviews-list" maxHeight="520px">
                            {(pluginData.reviews && pluginData.reviews.length > 0) ? (
                                <>
                                    {pluginData.reviews.slice(0, 5).map((review, index) => (
                                        <div key={index} className="flex flex-col gap-2 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
                                            <div className="flex items-center justify-between gap-3">
                                                <div className="flex items-center gap-2">
                                                    <FaUser className="text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                    <span className="font-semibold text-gray-900 dark:text-gray-100">{review.reviewer}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <StarRating rating={review.rating} maxRating={5}/>
                                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                                        {formatDate(review.created.timestamp * 1000)}
                                                    </span>
                                                </div>
                                            </div>
                                            {review.title && (
                                                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">{review.title}</h3>
                                            )}
                                            {review.content && (
                                                <div
                                                    className="prose prose-sm max-w-none text-gray-700 dark:prose-invert dark:text-gray-300"
                                                    dangerouslySetInnerHTML={{__html: review.content}}
                                                />
                                            )}
                                        </div>
                                    ))}

                                    <div className="pt-2">
                                        <button
                                            type="button"
                                            onClick={() => setIsOpen(true)}
                                            className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400"
                                        >
                                            View all {pluginData.num_ratings} reviews
                                        </button>
                                    </div>

                                    {isOpen && (
                                        <Overlay
                                            fullScreen={true}
                                            onClick={() => setIsOpen(false)}
                                            className={twMerge("bg-gray-900 dark:bg-gray-900 bg-opacity-60 dark:bg-opacity-90")}
                                        >
                                            <Modal onClose={() => setIsOpen(false)} type={EModalMode.FIT_CONTENT}>
                                                <ModalHeader onClose={() => setIsOpen(false)} closeCrossSize={6}>
                                                    <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 md:text-2xl">
                                                        All Reviews ({pluginData.num_ratings})
                                                    </h1>
                                                </ModalHeader>
                                                <ModalBody className="flex flex-col gap-4 min-h-[60vh] max-h-[80vh] overflow-auto">
                                                    <AllReviews pluginId={pluginData.pluginid}/>
                                                </ModalBody>
                                            </Modal>
                                        </Overlay>
                                    )}
                                </>
                            ) : (
                                <div className="text-center text-gray-500 dark:text-gray-400">
                                    No reviews available
                                </div>
                            )}
                        </Section>

                        <Section
                            title="Screenshots"
                            icon={<FaImages/>}
                            id="screenshots"
                            maxHeight="520px"
                            bodyClassName={twMerge("prose prose-sm sm:prose md:prose-lg dark:prose-invert")}
                            bodyStyle={{maxWidth: "100%"}}
                        >
                            {(pluginData.screenshots && pluginData.screenshots.trim().length > 0 &&
                                    <div dangerouslySetInnerHTML={{__html: pluginData.screenshots ?? ""}}/>)
                                || <div className="text-center text-gray-500 dark:text-gray-400">No screenshots available</div>}
                        </Section>

                        <Section
                            title="Changelog"
                            icon={<FaHistory/>}
                            id="changelog"
                            maxHeight="320px"
                            bodyClassName={twMerge("prose prose-sm sm:prose md:prose-lg dark:prose-invert")}
                            bodyStyle={{maxWidth: "100%"}}
                        >
                            {(pluginData?.changelog && pluginData.changelog.trim().length > 0 &&
                                    <div dangerouslySetInnerHTML={{__html: pluginData.changelog ?? ""}}/>)
                                || <div className="text-center text-gray-500 dark:text-gray-400">No changelog available</div>}
                        </Section>

                        <div className="flex justify-center pt-2">
                            <Link
                                href={backHref}
                                className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                            >
                                <FaArrowLeft className="h-4 w-4" aria-hidden="true"/> Back to plugins
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export const Section = (props: {
    title: string;
    icon: ReactElement;
    className?: string;
    children: ReactNode;
    id?: string;
    bodyClassName?: string;
    contentClassName?: string;
    maxHeight?: string;
    style?: CSSProperties;
    bodyStyle?: CSSProperties;
}) => {
    const id = props.id || props.title.toLowerCase().replace(' ', '-');
    const [isOpen, setIsOpen] = useState(false);
    const [hasOverflow, setHasOverflow] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const element = contentRef.current;
        if (!element) return;

        const update = () => {
            if (!props.maxHeight) {
                setHasOverflow(false);
                return;
            }
            setHasOverflow(element.scrollHeight > element.clientHeight + 1);
        };

        update();

        const resizeObserver = typeof ResizeObserver !== 'undefined'
            ? new ResizeObserver(() => update())
            : null;
        resizeObserver?.observe(element);

        window.addEventListener('resize', update);
        return () => {
            resizeObserver?.disconnect();
            window.removeEventListener('resize', update);
        };
    }, [props.maxHeight, props.children]);

    const shouldClamp = Boolean(props.maxHeight);

    return (
        <section
            id={id}
            className={twMerge("scroll-mt-24", props.className)}
            style={props.style}
        >
            <div
                className={twMerge(
                    "overflow-hidden rounded-2xl border border-gray-200 bg-white/80 shadow-sm backdrop-blur dark:border-gray-700 dark:bg-gray-900/40",
                    props.contentClassName
                )}
            >
                <div className="flex items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 dark:border-gray-700 md:px-6">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
                            {props.icon}
                        </div>
                        <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100 md:text-lg">
                            {props.title}
                        </h2>
                    </div>
                    {hasOverflow && props.maxHeight && (
                        <button
                            type="button"
                            onClick={() => setIsOpen(true)}
                            className="text-sm font-semibold text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                            See more
                        </button>
                    )}
                </div>

                <div className="relative px-4 py-4 md:px-6">
                    <div
                        ref={contentRef}
                        className={twMerge(
                            "relative",
                            shouldClamp ? "overflow-hidden" : "",
                            props.bodyClassName
                        )}
                        style={{...props.bodyStyle, maxHeight: shouldClamp ? props.maxHeight : undefined}}
                    >
                        {props.children}
                    </div>

                    {shouldClamp && hasOverflow && (
                        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent dark:from-gray-900 dark:to-transparent"/>
                    )}
                </div>
            </div>

            {isOpen && (
                <Overlay
                    fullScreen={true}
                    onClick={() => setIsOpen(false)}
                    className="bg-gray-900 bg-opacity-60 dark:bg-opacity-90"
                >
                    <Modal onClose={() => setIsOpen(false)} type={EModalMode.FIT_CONTENT}>
                        <ModalHeader onClose={() => setIsOpen(false)} closeCrossSize={6}>
                            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100 md:text-2xl">{props.title}</h1>
                        </ModalHeader>
                        <ModalBody
                            className={twMerge("flex max-h-[80vh] flex-col gap-4 overflow-auto", props.bodyClassName)}
                            style={{...props.bodyStyle}}
                        >
                            {props.children}
                        </ModalBody>
                    </Modal>
                </Overlay>
            )}
        </section>
    );
}

const AllReviews = ({ pluginId }: { pluginId: string }) => {
    const [page, setPage] = useState(1);
    const { reviews, isLoading, isError, hasNextPage, loadNextPage, total } = usePluginReviews(pluginId, page);

    if (isError) {
        return <div className="text-center text-red-500">Failed to load reviews</div>;
    }

    if (isLoading && reviews.length === 0) {
        return <div className="text-center">Loading reviews...</div>;
    }

    return (
        <div className="flex flex-col gap-4">
            {reviews.map((review, index) => (
                <div key={index} className={twMerge(
                    "review",
                    "flex flex-col gap-2",
                    "p-4",
                    "border border-gray-200 dark:border-gray-700 rounded-md"
                )}>
                    <div className={twMerge("review-header", "flex flex-row items-center justify-between")}>
                        <div className={twMerge("reviewer", "flex flex-row items-center gap-2")}>
                            <FaUser className="text-gray-500 dark:text-gray-400"/>
                            <span className="font-semibold">{review.reviewer}</span>
                        </div>
                        <div className={twMerge("rating", "flex flex-row items-center gap-2")}>
                            <StarRating rating={review.rating} maxRating={5}/>
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                                {formatDate(review.created.timestamp * 1000)}
                            </span>
                        </div>
                    </div>
                    {review.title && (
                        <h3 className="text-lg font-semibold">{review.title}</h3>
                    )}
                    {review.content && (
                        <div className="text-sm text-gray-700 dark:text-gray-300" 
                             dangerouslySetInnerHTML={{__html: review.content}}/>
                    )}
                </div>
            ))}

            {hasNextPage && (
                <button
                    onClick={() => {
                        setPage(p => p + 1);
                        loadNextPage();
                    }}
                    className={twMerge(
                        "mt-4 px-4 py-2",
                        "text-blue-500 dark:text-blue-400",
                        "hover:text-blue-600 dark:hover:text-blue-300",
                        "transition-colors duration-200",
                        "text-center font-semibold"
                    )}
                >
                    Load more reviews
                </button>
            )}
        </div>
    );
};

PluginPage.getLayout = function getLayout(page: ReactElement, props: any) {
    const baseUrl = String(process.env.NEXT_PUBLIC_WEBSITE_URL || "");
    const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl.slice(0, -1) : baseUrl;
    const canonicalUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/plugins/${props.pluginId}` : `/plugins/${props.pluginId}`;
    const rootUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/` : "/";
    const pluginsUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/plugins` : "/plugins";
    const imageUrl = normalizedBaseUrl ? `${normalizedBaseUrl}/catspyjamas.jpg` : "/catspyjamas.jpg";

    const nowSeconds = typeof props.renderedAt === "number" ? props.renderedAt / 1000 : null;
    const daysSinceUpdate = nowSeconds !== null && typeof props.lastUpdatedTimestamp === "number"
        ? Math.max(0, Math.floor((nowSeconds - props.lastUpdatedTimestamp) / 86400))
        : null;

    const descriptionParts = [
        `Analyze ${props.pluginName}: ratings, downloads, installs, maintenance signals, compatibility, comparisons, and alternatives.`,
        typeof props.rating === "number" && typeof props.numRatings === "number"
            ? `Rating ${props.rating.toFixed(1)}/5 from ${formatNumber(props.numRatings)} reviews.`
            : null,
        typeof props.activeInstalls === "number" ? `${formatNumber(props.activeInstalls)} active installs.` : null,
        typeof props.downloaded === "number" ? `${formatNumber(props.downloaded)} downloads.` : null,
        daysSinceUpdate !== null ? `Updated ${daysSinceUpdate} days ago.` : null,
        props.minimumWordpressVersion ? `Requires WordPress ${props.minimumWordpressVersion}.` : null,
        props.testedWordpressVersion ? `Tested up to WP ${props.testedWordpressVersion}.` : null,
        props.minimumPhpVersion ? `Requires PHP ${props.minimumPhpVersion}.` : null,
    ].filter(Boolean);

    const rawDescription = descriptionParts.join(' ');
    const description = rawDescription.length > 280 ? `${rawDescription.slice(0, 277)}...` : rawDescription;

    const stripHtml = (value: string): string => value.replace(/<[^>]*>/g, "").trim();
    const authorName = typeof props.authorName === "string" ? stripHtml(props.authorName) : "";
    const downloadUrl = typeof props.wordpressUrl === "string" && props.wordpressUrl ? props.wordpressUrl : (
        typeof props.marketplaceUrl === "string" && props.marketplaceUrl ? props.marketplaceUrl : undefined
    );
    const dateModified = typeof props.lastUpdatedTimestamp === "number"
        ? new Date(props.lastUpdatedTimestamp * 1000).toISOString()
        : undefined;

    const jsonLd: Record<string, any>[] = [
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "Home",
                    "item": rootUrl,
                },
                {
                    "@type": "ListItem",
                    "position": 2,
                    "name": "Plugins",
                    "item": pluginsUrl,
                },
                {
                    "@type": "ListItem",
                    "position": 3,
                    "name": props.pluginName,
                    "item": canonicalUrl,
                },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": props.pluginName,
            "url": canonicalUrl,
            "description": description,
            "operatingSystem": "WordPress",
            "applicationCategory": "WordPress Plugin",
            "softwareVersion": props.version || undefined,
            "dateModified": dateModified,
            "downloadUrl": downloadUrl,
            "author": authorName ? { "@type": "Person", "name": authorName } : undefined,
            "sameAs": [
                typeof props.homepage === "string" && props.homepage ? props.homepage : null,
                typeof props.wordpressUrl === "string" && props.wordpressUrl ? props.wordpressUrl : null,
            ].filter(Boolean),
            "aggregateRating": (
                typeof props.rating === "number"
                && typeof props.numRatings === "number"
                && props.numRatings > 0
                && props.rating > 0
            ) ? {
                "@type": "AggregateRating",
                "ratingValue": Number(props.rating.toFixed(2)),
                "ratingCount": props.numRatings,
                "bestRating": 5,
                "worstRating": 1,
            } : undefined,
        },
    ];
    return (<Layout
        title={`${props.pluginName} Plugin Stats: Reviews, Installs & Maintenance`}
        description={description || `${props.pluginName} plugin stats and analysis: reviews, downloads, installs, maintenance score, and compatibility.`}
        url={canonicalUrl}
        image={imageUrl}
        jsonLd={jsonLd}
    >
        {page}
    </Layout>)
}

export default PluginPage
